export const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

export const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);

// Provincial identifiers used on normal and personalised registrations.
export const PROVINCE_MARKS = ["GP", "EC", "FS", "MP", "NW", "NC", "L", "ZN", "WP"];

const NORMAL_PATTERNS = [
  // Gauteng: the earlier ABC 123 GP allocation and current AB 12 CD GP series.
  { name: "gauteng", regex: /^(?:[A-Z]{3}\d{3}|[A-Z]{2}\d{2}[A-Z]{2})GP$/ },
  // Eastern Cape, Free State, Mpumalanga, North West and Northern Cape.
  { name: "provincial", regex: /^[A-Z]{3}\d{3}(?:EC|FS|MP|NW|NC)$/ },
  // Limpopo uses the single-letter provincial identifier L.
  { name: "limpopo", regex: /^[A-Z]{3}\d{3}L$/ },
  // KwaZulu-Natal's system introduced in December 2023: AB 12 CD ZN.
  { name: "kwazulu-natal", regex: /^[A-Z]{2}\d{2}[A-Z]{2}ZN$/ },
  // Western Cape and still-valid legacy municipal series, e.g. CA 123-456,
  // CAA 123-456, CY 12345, ND 123456 and NP 12345.
  { name: "legacy-local", regex: /^[A-Z]{1,3}\d{1,6}$/ },
  // DIRCO diplomatic/consular/international-organisation registrations.
  { name: "diplomatic", regex: /^(?:D\d{1,6}D|[CXS]\d{1,7}D)$/ },
];

const PERSONALISED_PATTERN = /^([A-Z0-9]{1,7})(GP|EC|FS|MP|NW|NC|L|ZN|WP)$/;

/** Remove spaces, hyphens and plate artwork while retaining registration characters. */
export function cleanPlate(input = "") {
  return String(input).toUpperCase().replace(/[^A-Z0-9]+/g, "");
}

/** Return the recognised SA registration family, or null when invalid. */
export function getPlateFormat(input = "") {
  const plate = cleanPlate(input);
  const normal = NORMAL_PATTERNS.find(({ regex }) => regex.test(plate));
  if (normal) return normal.name;
  if (PERSONALISED_PATTERN.test(plate)) return "personalised";
  // Registration marks have changed repeatedly and personalised allocations
  // are intentionally flexible. Keep unfamiliar but plausible values usable.
  if (/^[A-Z0-9]{3,10}$/.test(plate)) return "generic";
  return null;
}

export function isValidPlate(input = "") {
  return getPlateFormat(input) !== null;
}

function groupCharacters(value) {
  return value.match(/[A-Z]+|\d+/g)?.join(" ") || value;
}

/**
 * Normalise all supported SA plate families for display. Separators are not
 * significant when sending a registration to the API.
 */
export function formatNrPlate(input = "") {
  const plate = cleanPlate(input);
  if (!plate) return "";

  const province = PROVINCE_MARKS.find((mark) => plate.endsWith(mark));
  if (province && plate.length > province.length) {
    const registration = plate.slice(0, -province.length);
    return `${groupCharacters(registration)} ${province}`;
  }

  return groupCharacters(plate);
}
